import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useCountdown } from '../hooks/use-countdown';
import { useZeitwahl } from '../provider';
import { formatCountdown } from '../time/format';

export interface HoldCountdownProps {
  /** `hold.expiresAt`; renders nothing without it. */
  expiresAt: string | null | undefined;
  onExpire?: () => void;
}

/**
 * Visible `m:ss` timer plus a polite live region that only speaks at milestones (start, every
 * minute under five, one minute, thirty seconds, expiry) so screen readers never hear a ticking clock.
 */
export function HoldCountdown({ expiresAt, onExpire }: HoldCountdownProps): ReactNode {
  const { t } = useZeitwahl();
  const remaining = useCountdown(expiresAt);
  const [announcement, setAnnouncement] = useState('');
  const expiredFor = useRef<string | null>(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!expiresAt || remaining === null) return;
    if (remaining <= 0) {
      if (expiredFor.current !== expiresAt) {
        expiredFor.current = expiresAt;
        setAnnouncement(t('holdExpired'));
        onExpireRef.current?.();
      }
      return;
    }
    if (remaining === 60) setAnnouncement(t('holdAnnounceOneMinute'));
    else if (remaining === 30) setAnnouncement(t('holdAnnounceSeconds', { seconds: 30 }));
    else if (remaining % 60 === 0 && remaining <= 300) {
      setAnnouncement(t('holdAnnounceMinutes', { minutes: remaining / 60 }));
    }
  }, [expiresAt, remaining, t]);

  // Announce the initial duration once per hold, not on every tick (hence only `expiresAt`).
  const initialRemaining = useRef(remaining);
  initialRemaining.current = remaining;
  useEffect(() => {
    const r = initialRemaining.current;
    if (expiresAt && r !== null && r > 0) {
      setAnnouncement(t('holdAnnounceStart', { minutes: Math.max(1, Math.round(r / 60)) }));
    }
  }, [expiresAt, t]);

  if (!expiresAt || remaining === null) return null;
  return (
    <div
      className={`zw-hold${remaining <= 60 ? ' zw-hold--urgent' : ''}${remaining <= 0 ? ' zw-hold--expired' : ''}`}
    >
      <span className="zw-hold-text" role="timer">
        {remaining > 0
          ? t('holdRemaining', { time: formatCountdown(remaining) })
          : t('holdExpired')}
      </span>
      <span className="zw-sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
    </div>
  );
}
